import { isWebUrl, launchRepeatOf, type LaunchRepeat } from '../launch-flow/launch'

/** The video page: the player for a video activity just launched. */
export const videoPath = '/launch/video'

/** What the video button hands the video page, in the history entry's state. */
export interface VideoState {
  readonly url: string
  readonly name: string
  /** Where Back goes: the page the video was launched from. */
  readonly back: string
  /** Asks for the launch again, for a fresh link when the one playing has expired. */
  readonly repeat: LaunchRepeat
}

const mediaExtensions = ['.mp4', '.m4v', '.webm', '.ogv', '.ogg', '.mov']

/**
 * Whether the URL is a media file the HTML5 player plays. Anything else, such as a video service's
 * player page (Catalog's Choose Video stores Ravnur's), opens as a page of its own.
 */
export function isMediaFile(url: string): boolean {
  try {
    const path = new URL(url).pathname.toLowerCase()
    return mediaExtensions.some((extension) => path.endsWith(extension))
  } catch {
    return false
  }
}

/**
 * The video page's state, or `null` when the page was opened some other way or the state is not
 * one the button gives: a web URL to play, a path on this site to go back to, and the launch to ask
 * for again.
 */
export function videoStateOf(state: unknown): VideoState | null {
  if (typeof state !== 'object' || state === null) {
    return null
  }
  const { url, name, back, repeat } = state as Partial<Record<keyof VideoState, unknown>>
  const again = launchRepeatOf(repeat)
  return typeof url === 'string' && isWebUrl(url) && typeof name === 'string' && typeof back === 'string' && /^\/(?![/\\])/.test(back) && again !== null
    ? { url, name, back, repeat: again }
    : null
}
